// Define the types for our events and listeners for full type safety.
type BusEvent = unknown[];
type BusListener<T extends BusEvent = BusEvent> = (...args: T) => void;
type BusEventStore = Map<symbol | string, Set<BusListener>>;

/**
 * A reactive, type-safe, global event bus for Nuxt 4 applications.
 *
 * It uses a singleton pattern to ensure a single bus instance is shared
 * across the entire app. It also features automatic listener cleanup
 * by leveraging Vue's `onUnmounted` lifecycle hook.
 */
export const useEventBus = () => {
  // The 'listeners' map is created only once and shared across all
  // calls to useEventBus(), creating the singleton instance.
  const listeners: BusEventStore = globalThis.__event_bus__ || (globalThis.__event_bus__ = new Map());

  /**
   * Subscribes to an event.
   *
   * Automatically unsubscribes when the component is unmounted.
   *
   * @param event - The event identifier.
   * @param listener - The callback function to execute when the event is emitted.
   */
  const on = <T extends BusEvent>(event: symbol | string, listener: BusListener<T>) => {
    // Add the event to the map if it doesn't exist.
    if (!listeners.has(event)) {
      listeners.set(event, new Set());
    }

    const eventListeners = listeners.get(event)!;
    eventListeners.add(listener as BusListener);

    // The unsubscribe function.
    const unsubscribe = () => {
      const eventListeners = listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(listener as BusListener);
        // Clean up the Set and Map if they become empty.
        if (eventListeners.size === 0) {
          listeners.delete(event);
        }
      }
    };

    // If the listener is attached inside a component instance,
    // automatically unsubscribe when the component is unmounted.
    if (getCurrentInstance()) {
      onUnmounted(unsubscribe);
    }

    // Return the unsubscribe function for manual control if needed.
    return { unsubscribe };
  };

  /**
   * Emits an event to all active listeners.
   *
   * @param event - The event identifier.
   * @param args - The payload to pass to the listeners.
   */
  const emit = <T extends BusEvent>(event: symbol | string, ...args: T) => {
    if (listeners.has(event)) {
      // Call all listeners for the given event with the provided arguments.
      listeners.get(event)!.forEach(listener => listener(...args));
    }
  };

  /**
   * Unsubscribes from an event.
   *
   * @param event - The event identifier.
   * @param listener - The callback function to unsubscribe from.
   */
  const off = <T extends BusEvent>(event: symbol | string, listener: BusListener<T>) => {
    if (listeners.has(event)) {
      listeners.get(event)!.delete(listener as BusListener);
    }
  };

  return {
    on,
    emit,
    off,
  };
};

// This part is for HMR (Hot Module Replacement)
// It doesn't wipe our event bus instance during development.
declare global {
  var __event_bus__: BusEventStore;
}
